import React from "react"
import styles from "./SpendingsListPage.module.scss"
import { observer } from "mobx-react"
import { SpendingsExternalParams, SummaryTable } from "./Table"
import { Page } from "modules/common/components/page/Page"
import { SpendingsListType } from "../models/spending-list"
import { Filters } from "./Filters"
import { Loader } from "modules/common/components/loader/Loader"
import { DetailsPopup } from "modules/common/components/form/DetailsPopup"
import { SpendingForm } from "./SpendingForm"
import { Model, model } from "./validation"
import { isNotNull } from "modules/common/services/typescript"
import { formatDate, parseDate } from "modules/common/services/formatting/date"
import { SpendingsListRowSnapshotType } from "../models/spending-list-row"
import { toString, trimStart } from "modules/common/services/strings"
import { SessionType } from "modules/session/auth/models/session"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { Money } from "modules/common/components/money/Money"
import { routes } from "modules/common/routes"
import { SPENDING_SECTION } from "./filter-params"
import { parse } from "query-string"
import { can } from "modules/session/auth/access"
import func from "../../functionalities"
import { Button, InputGroup } from "@blueprintjs/core"
import { withNavigation, NavigationProps } from "modules/common/components/routing/withNavigation"

function isSpendingIdInHash(locationHash: string) {
    const hash = trimStart("#", locationHash)

    if (hash) {
        const metadata = parse(hash)
        const spending = (metadata[SPENDING_SECTION] as string) || ""
        if (spending && spending !== "") {
            return true
        }
    }

    return false
}

class Base extends React.Component<SpendingsListPageProps & NavigationProps, SpendingsListPageState> {
    constructor(props: any) {
        super(props)

        this.state = { editable: null, selected: null, isSpendingId: false }
    }

    async componentDidMount() {
        document.title = "Расходы"
        const { store, location, navigate } = this.props
        store.init()

        // фильтр из ссылки (дашборд, накладные) важнее запомненного; без ссылки остаётся
        // то, что пользователь выбрал раньше, — стор живёт всё время работы приложения
        const externals = SpendingsExternalParams(location.hash)
        const isId = isSpendingIdInHash(location.hash)
        if (externals) {
            store.setupFilter(externals)
        } else if (store.filters.spendingId) {
            store.restoreFilters()
        }

        // ссылка на конкретную операцию остаётся в адресе, остальные уже применены.
        // navigate из componentDidMount роутер игнорирует (его эффект ещё не отработал) —
        // откладываем до конца коммита
        if (location.hash && !isId) {
            setTimeout(() => navigate(routes.expenses.path, { replace: true }))
        }

        this.setState({ isSpendingId: isId })
        await store.loadTable()
    }

    render() {
        const { store, session } = this.props
        const { editable, selected } = this.state

        return (
            <Page className={styles.page}>
                {isNotNull(editable) && (
                    <DetailsPopup title={"Операция"} onCancel={this.onCreationCancel} width={700}>
                        <SpendingForm
                            baseUrl={store.baseUrl}
                            orders={store.orders}
                            types={store.overheadTypes}
                            requests={store.requests}
                            model={editable}
                            row={selected}
                            store={store}
                            onSubmit={this.onSubmit}
                            onSaved={this.onCreationCancel}
                        />
                    </DetailsPopup>
                )}

                <Loader active={store.isLoading} />

                {!this.state.isSpendingId && (
                    <>
                        <div className="header-block">
                            <Filters store={store} session={session} />
                        </div>
                        {can(func.SPENDINGS_EDIT, session.access) && (
                            <PlanrButton
                                type="secondary"
                                icon="general-plus-big"
                                round={true}
                                onClick={this.createRow}
                                className={styles.addButton}
                            />
                        )}
                    </>
                )}
                <div className={`${styles.tools} `}>
                    {can(func.SPENDINGS_EXPORT, session.access) && (
                        <PlanrButton
                            type="graybtn"
                            icon="general-export"
                            round={true}
                            onClick={this.props.store.export}
                        />
                    )}
                    <PlanrButton type="graybtn" icon="general-print" round={true} onClick={this.props.store.print} />

                    <InputGroup
                        className={`planr-round-input`}
                        onChange={this.onChange}
                        onKeyDown={this.onSearchKeyDown}
                        placeholder={"Поиск"}
                        value={store.query}
                        style={{ marginLeft: "16px" }}
                        rightElement={
                            store.query ? (
                                <Button minimal={true} icon="cross" onClick={this.clearSearch} title="Сбросить поиск" />
                            ) : undefined
                        }
                    />
                    <PlanrButton
                        type="secondary"
                        icon="general-search"
                        size="small"
                        style={{ marginLeft: "24px" }}
                        onClick={this.onSearch}
                    />
                </div>

                {this.state.isSpendingId && (
                    <div className={styles.resetPageBtn}>
                        <PlanrButton
                            type="blueish"
                            onClick={this.showAll}
                            size={"small"}
                        >
                            Показать все
                        </PlanrButton>
                    </div>
                )}

                <div className={styles.SumValue}>
                    <div className={`color-green`} style={{ position: "absolute", left: "100px", width: "105px" }}>
                        <Money className="money " amount={store.incomingSum} />
                    </div>
                    <div className={`color-red`} style={{ position: "absolute", left: "220px", width: "105px" }}>
                        <Money className="money " amount={store.spendingSum} />
                    </div>
                </div>

                <SummaryTable store={store} onSelect={this.editRow} />
            </Page>
        )
    }

    onSubmit = (body: Model) => {
        return this.props.store.saveRow({
            ...body,
            date: formatDate(body.date),
        })
    };
    onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.store.setQuery(e.currentTarget.value)
    };

    onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            this.onSearch()
        }
    };

    onSearch = () => {
        this.props.store.queryChanged(this.props.store.query)
    };

    clearSearch = () => {
        this.props.store.queryChanged("")
    };

    createRow = () => {
        this.setState({ editable: model(), selected: null })
    };

    showAll = async () => {
        this.props.navigate(routes.expenses.path, { replace: true })
        this.props.store.restoreFilters()
        this.setState({ isSpendingId: false })
        await this.props.store.loadTable()
    };

    editRow = (row: SpendingsListRowSnapshotType) => {
        // без права записи реестр только читается: форму операции не открываем
        if (!can(func.SPENDINGS_EDIT, this.props.session.access)) {
            return
        }

        this.setState({
            editable: {
                id: row.id,
                date: parseDate(row.date) || null,
                incoming: toString(row.isIncoming),
                name: row.name,
                payloadId: row.payload || row.dictionaryId,
                tempPayloadId: row.dictionaryId,
                sum: row.sum,
                type: row.type,
                isCash: row.isCash,
                comment: row.comment,
                document: row.document,
                spending: "",
            },
            selected: row,
        })
    };

    onCreationCancel = () => this.setState({ editable: null, selected: null });
}

export const SpendingsListPage = withNavigation(observer(Base))

interface SpendingsListPageProps {
    store: SpendingsListType
    session: SessionType
}

interface SpendingsListPageState {
    editable: Model | null
    selected: SpendingsListRowSnapshotType | null
    isSpendingId: boolean
}
